import { useState } from "react";
import { FilePdf, UploadSimple, Trash, FileArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Field, Section } from "./fields";
import { Label } from "../ui/label";

export const emptyForm1 = {
  full_name: "", dob: "", service_address: "", county: "", primary_phone: "", can_text: "",
  email: "", preferred_communication: "", preferred_language: "",
  has_legal_rep: "", rep_type: "", rep_name: "", rep_relationship: "", rep_phone: "", rep_email: "",
  directs_own_care: "",
  primary_contact_type: "", pc_name: "", pc_relationship: "", pc_phone: "", pc_email: "",
  ec_name: "", ec_relationship: "", ec_phone: "", ec_email: "",
  payer: "", mco_name: "", medicaid_id: "", mco_member_id: "",
  coordinator_name: "", coordinator_phone: "", coordinator_email: "",
  caregiver_referral: "", referral_name: "", referral_phone: "", referral_email: "", referral_notes: "",
};

const Radio = ({ label, options, value, onChange, tid }) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <div className="flex flex-wrap gap-1.5" data-testid={tid}>
      {options.map((o) => (
        <button key={o} type="button" onClick={() => onChange(value === o ? "" : o)}
          data-testid={`${tid}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={`rounded-md border px-3 py-1.5 text-sm transition-all ${
            value === o ? "border-primary bg-primary/10 font-medium text-primary" : "border-stone-200 bg-white text-stone-500 hover:border-stone-400 hover:text-stone-800"
          }`}>{o}</button>
      ))}
    </div>
  </div>
);

export default function Form1Form({ data, onChange, clientId, clientCode, publicMode = false }) {
  const [poaBusy, setPoaBusy] = useState(false);
  const set = (k) => (e) => onChange({ ...data, [k]: e.target.value });
  const setR = (k) => (v) => onChange({ ...data, [k]: v });

  const uploadPoa = async (file) => {
    if (!file) return;
    setPoaBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: res } = await api.post(`/clients/${clientId}/poa-document`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ ...data, poa_document: res.file });
      toast.success("POA/guardianship document uploaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPoaBusy(false);
    }
  };

  const removePoa = async () => {
    try {
      await api.delete(`/clients/${clientId}/poa-document`);
      onChange({ ...data, poa_document: null });
      toast.success("Document removed");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="form1-form">
      {!publicMode && (
      <div className="rounded-lg border border-[#C0392B]/30 bg-[#C0392B]/5 p-4 text-sm text-stone-700" data-testid="phi-notice">
        <span className="font-semibold text-[#C0392B]">STAFF USE ONLY — contains PHI.</span>{" "}
        This is the master record and the only form with identifying information. All other forms link via the auto-generated client code.
      </div>
      )}

      {clientId ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-stone-200 bg-white p-4 shadow-sm">
          <span className="text-sm text-stone-500" data-testid="form1-no-code-note">The client code is intentionally not shown on this form — it links only the non-identifying forms.</span>
          <a href={`${api.defaults.baseURL}/clients/${clientId}/forms/intake/pdf`} download
            className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-md border border-stone-300 px-3 text-xs font-medium text-stone-700 transition-colors hover:bg-stone-900 hover:text-white"
            data-testid="form1-pdf-button">
            <FilePdf size={14} /> Download PDF
          </a>
        </div>
      ) : (
        <p className="rounded-lg border border-stone-200 bg-accent p-3 text-xs text-stone-600" data-testid="form1-prereq-note">
          The client code is auto-generated on save. PDF download and POA document upload become available after the client is created.
        </p>
      )}

      <Section title="Client">
        <Field label="Client full name *" tid="f1-full-name" value={data.full_name} onChange={set("full_name")} />
        <Field label="Date of birth" tid="f1-dob" type="date" value={data.dob} onChange={set("dob")} />
        <Field label="Service address" tid="f1-service-address" value={data.service_address} onChange={set("service_address")} />
        <Field label="County" tid="f1-county" value={data.county} onChange={set("county")} />
        <Field label="Primary phone" tid="f1-primary-phone" value={data.primary_phone} onChange={set("primary_phone")} />
        <Radio label="Can we text?" options={["Yes", "No"]} value={data.can_text} onChange={setR("can_text")} tid="f1-can-text" />
        <Field label="Email" tid="f1-email" value={data.email} onChange={set("email")} />
        <Radio label="Preferred communication method" options={["Phone call", "Text", "Email", "Any"]} value={data.preferred_communication} onChange={setR("preferred_communication")} tid="f1-preferred-communication" />
        <Field label="Preferred language" tid="f1-preferred-language" value={data.preferred_language} onChange={set("preferred_language")} />
      </Section>

      <Section title="Legal / Authorized Representative">
        <Radio label="Does the client have a legal authorized representative?" options={["Yes", "No"]} value={data.has_legal_rep} onChange={setR("has_legal_rep")} tid="f1-has-legal-rep" />
        <Radio label="Does the client direct their own care?" options={["Yes", "No"]} value={data.directs_own_care} onChange={setR("directs_own_care")} tid="f1-directs-own-care" />
        {data.has_legal_rep === "Yes" && (
          <>
            <Radio label="Type" options={["POA", "Guardian", "Other"]} value={data.rep_type} onChange={setR("rep_type")} tid="f1-rep-type" />
            <Field label="Representative name" tid="f1-rep-name" value={data.rep_name} onChange={set("rep_name")} />
            <Field label="Relationship" tid="f1-rep-relationship" value={data.rep_relationship} onChange={set("rep_relationship")} />
            <Field label="Phone" tid="f1-rep-phone" value={data.rep_phone} onChange={set("rep_phone")} />
            <Field label="Email" tid="f1-rep-email" value={data.rep_email} onChange={set("rep_email")} />
            <div className="space-y-1.5 md:col-span-2">
              <Label>Upload POA / guardianship document</Label>
              {data.poa_document ? (
                <div className="flex items-center gap-2 rounded-lg border border-stone-200 bg-accent/60 px-3 py-2 text-xs text-stone-700" data-testid="f1-poa-file">
                  <FilePdf size={16} className="shrink-0 text-[#C0392B]" />
                  <span className="min-w-0 flex-1 truncate">{data.poa_document.filename}</span>
                  <a href={`${api.defaults.baseURL}/clients/${clientId}/poa-document`} target="_blank" rel="noreferrer"
                    className="shrink-0 font-medium text-primary hover:text-stone-900" data-testid="f1-poa-download"><FileArrowDown size={16} /></a>
                  <button type="button" onClick={removePoa} className="shrink-0 text-stone-400 hover:text-[#C0392B]" data-testid="f1-poa-delete"><Trash size={15} /></button>
                </div>
              ) : clientId ? (
                <label className={`inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-md border border-stone-300 px-3 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-900 hover:text-white ${poaBusy ? "opacity-50" : ""}`} data-testid="f1-poa-upload">
                  <UploadSimple size={15} /> {poaBusy ? "Uploading..." : "Choose file (PDF or image)"}
                  <input type="file" accept=".pdf,image/*" hidden disabled={poaBusy} onChange={(e) => uploadPoa(e.target.files?.[0])} data-testid="f1-poa-input" />
                </label>
              ) : (
                <p className="text-xs text-stone-400">Available after the client is created.</p>
              )}
            </div>
          </>
        )}
      </Section>

      <Section title="Primary Contact">
        <Radio label="Who is the primary contact?" options={["Client", "Authorized Representative", "Other"]} value={data.primary_contact_type} onChange={setR("primary_contact_type")} tid="f1-primary-contact-type" />
        {data.primary_contact_type === "Other" && (
          <>
            <Field label="Name" tid="f1-pc-name" value={data.pc_name} onChange={set("pc_name")} />
            <Field label="Relationship" tid="f1-pc-relationship" value={data.pc_relationship} onChange={set("pc_relationship")} />
            <Field label="Phone" tid="f1-pc-phone" value={data.pc_phone} onChange={set("pc_phone")} />
            <Field label="Email" tid="f1-pc-email" value={data.pc_email} onChange={set("pc_email")} />
          </>
        )}
      </Section>

      <Section title="Emergency Contact">
        <Field label="Name" tid="f1-ec-name" value={data.ec_name} onChange={set("ec_name")} />
        <Field label="Relationship" tid="f1-ec-relationship" value={data.ec_relationship} onChange={set("ec_relationship")} />
        <Field label="Phone" tid="f1-ec-phone" value={data.ec_phone} onChange={set("ec_phone")} />
        <Field label="Email" tid="f1-ec-email" value={data.ec_email} onChange={set("ec_email")} />
      </Section>

      <Section title="Payer / Program">
        <Radio label="Payer / Program" options={["CHC", "Private Pay", "Veterans", "LTC Insurance", "Other"]} value={data.payer} onChange={setR("payer")} tid="f1-payer" />
        <Field label="MCO (if applicable)" tid="f1-mco-name" value={data.mco_name} onChange={set("mco_name")} />
        <Field label="Medicaid ID (if applicable)" tid="f1-medicaid-id" value={data.medicaid_id} onChange={set("medicaid_id")} />
        <Field label="MCO Member ID (if applicable)" tid="f1-mco-member-id" value={data.mco_member_id} onChange={set("mco_member_id")} />
        <Field label="Service Coordinator name" tid="f1-coordinator-name" value={data.coordinator_name} onChange={set("coordinator_name")} />
        <Field label="Service Coordinator phone" tid="f1-coordinator-phone" value={data.coordinator_phone} onChange={set("coordinator_phone")} />
        <Field label="Service Coordinator email" tid="f1-coordinator-email" value={data.coordinator_email} onChange={set("coordinator_email")} />
      </Section>

      <Section title="Caregiver Referral">
        <Radio label="Did the client refer a caregiver for us to consider hiring?" options={["Yes", "No"]} value={data.caregiver_referral} onChange={setR("caregiver_referral")} tid="f1-caregiver-referral" />
        {data.caregiver_referral === "Yes" && (
          <>
            <div className="md:col-span-2 rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/5 p-3 text-xs text-stone-700" data-testid="f1-referral-legal-note">
              Legal restriction: we cannot employ anyone who is the client's spouse, holds Power of Attorney, serves as legal guardian, or acts as representative payee or other authorized decision-maker.
            </div>
            <Field label="Referral name" tid="f1-referral-name" value={data.referral_name} onChange={set("referral_name")} />
            <Field label="Referral phone" tid="f1-referral-phone" value={data.referral_phone} onChange={set("referral_phone")} />
            <Field label="Referral email" tid="f1-referral-email" value={data.referral_email} onChange={set("referral_email")} />
            <Field label="Referral notes" tid="f1-referral-notes" value={data.referral_notes} onChange={set("referral_notes")} />
          </>
        )}
      </Section>
    </div>
  );
}
